import React, { useState } from 'react'
import { Modal } from './ui/Modal'
import { MotifId } from '../types'
import { MOTIFS } from '../data/motifs'
import { COLOR_VALUES } from '../data/tailwindColors'

interface CreateSystemModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (name: string, motifId: MotifId) => void
}

export function CreateSystemModal({ isOpen, onClose, onCreate }: CreateSystemModalProps) {
  const [name, setName] = useState('')
  const [selectedMotif, setSelectedMotif] = useState<MotifId>('professional')

  const handleCreate = () => {
    if (!name.trim()) return
    onCreate(name.trim(), selectedMotif)
    setName('')
    setSelectedMotif('professional')
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleCreate()
    }
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} title="Create Design System" size="lg">
      <div className="space-y-6">
        {/* Name Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">System Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="My Design System"
            autoFocus
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>

        {/* Motif Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">Starting Preset</label>
          <div className="grid grid-cols-2 gap-3">
            {MOTIFS.map((motif) => {
              const isSelected = motif.id === selectedMotif
              const primaryColor = COLOR_VALUES[motif.tokens.primaryColor]

              return (
                <button
                  key={motif.id}
                  onClick={() => setSelectedMotif(motif.id)}
                  className={`
                    relative p-4 rounded-lg border-2 text-left transition-all cursor-pointer
                    ${
                      isSelected
                        ? 'border-gray-900 bg-gray-50 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-md flex-shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">{motif.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {motif.description}
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <svg
                        className="w-5 h-5 text-gray-900"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            Create System
          </button>
        </div>
      </div>
    </Modal>
  )
}
